import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why does DGRO have a lower yield than SCHD?',
    a: "DGRO intentionally filters out the top 10% highest-yielding companies in the market to avoid distressed firms. It also caps payout ratios at 75%. Those two screens together push DGRO toward lower-yielding tech and healthcare names with strong balance sheets, producing a trailing yield around 2.0%. SCHD has no such ceiling — it ranks survivors by yield within its quality screen and ends up with names paying closer to 3.4%.",
  },
  {
    q: 'Do SCHD and DGRO pay qualified dividends?',
    a: 'Yes. Unlike covered-call ETFs, both funds hold underlying equities directly with no option overlays. The vast majority of their distributions are classified as qualified dividend income, which means long-term capital-gains rates apply for investors who meet the 60-day holding period. This makes both funds highly tax-efficient for standard taxable brokerage accounts.',
  },
  {
    q: 'How much overlap is there between SCHD and DGRO?',
    a: "Despite both being broad U.S. dividend funds, the rulebooks pull them in different directions. SCHD's 100-stock quality-and-yield screen and DGRO's 5-year-growth + payout-ratio screen agree on only a small subset of names. By portfolio weight, overlap typically sits around 15-25% depending on rebalance timing — meaning holding both does NOT create heavy redundancy.",
  },
  {
    q: 'Which fund has the higher historical dividend growth rate?',
    a: "Counter-intuitively, SCHD's trailing 5-year dividend growth rate has historically run slightly higher than DGRO's, despite DGRO being branded as the growth-focused fund. That's because SCHD pulls in fast-growing financials and industrials whose payouts compound quickly. DGRO holds slower-growing mega-caps like Microsoft and Apple — they DO grow their dividends, but from a small base. Always pull the 5-Year DGR card in the DiviDrip modal to confirm with current data.",
  },
  {
    q: 'Can I own both SCHD and DGRO?',
    a: 'Yes, and the case is stronger here than with the SCHD/VYM pairing because of the lower weight overlap. A common split is 60% SCHD / 40% DGRO — the SCHD sleeve does the heavy income lifting while DGRO provides tech/healthcare exposure and a longer growth runway. Younger investors sometimes flip the ratio.',
  },
];

export default function LearnSchdVsDgro() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="SCHD vs DGRO — High Yield vs Balanced Dividend Growth (2026) | DiviDrip"
      description="SCHD vs DGRO side-by-side: yield, holdings, fees, index rules, overlap. Which dividend ETF fits your stage — and the case for owning both."
      canonical="https://twylightcrow.com/learn/schd-vs-dgro"
      guidePath="/learn/schd-vs-dgro"
      sectionLabel="Dividend Tiers & ETF Face-offs"
      sectionAnchor="tiers"
      kicker="Learn · ETF face-off"
      h1="SCHD vs DGRO — High Yield vs Balanced Dividend Growth"
      datePublished="2026-05-26"
      jsonLd={jsonLd}
      related={[
        { to: '/true-return/schd-vs-dgro', label: 'True Return Chart — SCHD vs DGRO, $10K after dividends and inflation' },
        { to: '/learn/schd-vs-vym', label: 'SCHD vs VYM — concentrated quality vs broad market yield' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/learn/evaluating-dividend-etfs', label: 'How to evaluate a dividend ETF' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers to check before any buy' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/screener', label: 'Stock Screener — filter by yield, sector, frequency' },
      ]}
    >
      <p>
        Two of the most dominant core dividend ETFs on the market approach income from
        completely different angles. Schwab&apos;s <strong>SCHD</strong> (Schwab U.S.
        Dividend Equity ETF) and BlackRock&apos;s <strong>DGRO</strong> (iShares Core
        Dividend Growth ETF) are often pitted against one another, but their rulebooks mean
        they rarely buy the same exact stocks. One hunts for{' '}
        <em>massive immediate cash flow</em>, while the other plays the long game of{' '}
        <em>compounding</em>.
      </p>

      <h2>Side-by-side</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Metric</th>
            <th className="text-left font-semibold">SCHD</th>
            <th className="text-left font-semibold">DGRO</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Underlying index</td>
            <td>Dow Jones U.S. Dividend 100</td>
            <td>Morningstar U.S. Dividend Growth</td>
          </tr>
          <tr>
            <td className="font-semibold">Distribution frequency</td>
            <td>Quarterly</td>
            <td>Quarterly</td>
          </tr>
          <tr>
            <td className="font-semibold">Trailing 12-month yield (approx)</td>
            <td>~3.3–3.6%</td>
            <td>~1.9–2.1%</td>
          </tr>
          <tr>
            <td className="font-semibold">Expense ratio</td>
            <td>0.06%</td>
            <td>0.08%</td>
          </tr>
          <tr>
            <td className="font-semibold">Number of holdings</td>
            <td>~100</td>
            <td>~410</td>
          </tr>
          <tr>
            <td className="font-semibold">Beta (5Y monthly, vs S&amp;P 500)</td>
            <td>~0.74</td>
            <td>~0.85</td>
          </tr>
          <tr>
            <td className="font-semibold">Best-fit use case</td>
            <td>Immediate income &amp; value</td>
            <td>Long-term total return</td>
          </tr>
        </tbody>
      </table>

      <h2>How the strategy filters work (60 seconds)</h2>
      <p>
        <strong>SCHD</strong> requires its holdings to have <em>10 consecutive years</em> of
        dividend payments. From that pool, it ranks them by financial-strength metrics
        (cash-flow-to-debt, return on equity, dividend yield, 5-year dividend growth) and
        selects the top 100. The result skews toward heavy-value sectors like Financials,
        Industrials, and Consumer Staples.
      </p>
      <p>
        <strong>DGRO</strong> takes a wider path. It requires only <em>5 years</em> of
        dividend growth, but adds two critical filters that SCHD does not have:
        it <strong>excludes the top 10% highest-yielding stocks</strong> in the market and
        <strong> caps payout ratios at 75%</strong>. Per BlackRock&apos;s prospectus, this
        intentional exclusion keeps the fund out of &quot;value traps&quot; and allows it to
        hold tech giants like Microsoft and Apple — companies paying small but rapidly
        growing dividends with strong balance sheets.
      </p>

      <h2>When to pick SCHD</h2>
      <ul>
        <li>You need higher current cash flow to pay bills or supplement active living expenses.</li>
        <li>
          You prefer a highly concentrated portfolio anchored in heavy-value sectors like
          Financials, Industrials, and Consumer Staples.
        </li>
        <li>
          You want a historically competitive dividend growth rate on top of an already high
          starting yield.
        </li>
      </ul>

      <h2>When to pick DGRO</h2>
      <ul>
        <li>You want a wider diversification cushion — roughly 410 holdings instead of 100.</li>
        <li>
          You&apos;re younger, in your wealth-accumulation phase, and want tech and
          healthcare exposure without fully abandoning dividend principles.
        </li>
        <li>
          You prioritise low payout ratios, which signal maximum long-term safety against
          dividend cuts and leave room for sustained growth.
        </li>
      </ul>

      <h2>The case for owning both</h2>
      <p>
        Because of their divergent criteria, SCHD and DGRO feature surprisingly low overlap
        — typically <strong>around 15-25% by portfolio weight</strong>, depending on
        rebalance timing. That means holding both does NOT result in redundant exposure.
        DGRO acts as an engine for share-price appreciation during bull markets thanks to
        its tech/healthcare tilt, while SCHD acts as your defensive, cash-generating heavy
        lifter through dividend-paying value names. The pair behaves more like
        complementary halves of a single dividend portfolio than two competing options.
      </p>

      <h2>Compare them live</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search <strong>SCHD</strong>, click the
        row to open the Stock Modal, then tap the <em>Compare</em> button and pick DGRO as
        slot B. The Overlay Chart will show the steep yield gap between the two funds, but
        watch the 5-Year Dividend Growth Rate card just below — DGRO&apos;s lower starting
        yield doesn&apos;t always translate into a faster growth rate, which is the single
        most important nuance when deciding whether DGRO actually earns its &quot;growth&quot;
        label for your timeline.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not investment advice. Yields, holdings, and expense ratios can
        change; always check the fund&apos;s latest factsheet at Schwab or iShares and the
        live numbers in DiviDrip before buying.
      </p>
    </LearnPage>
  );
}
